"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TermsSections() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: "Informational Purpose Only",
    answer: "The content on this Site is provided for general informational purposes only. Nothing on this Site constitutes financial, investment, legal, tax, or accounting advice. No information on this Site should be construed as a solicitation or offer to buy or sell securities or other financial instruments. Securities transactions are conducted exclusively through GT Securities, Inc., Member FINRA/SIPC. Please consult qualified advisors before making any financial decisions."
  }, {
    question: "No Advisory Relationship",
    answer: "Your use of this Site, submission of an inquiry form, or receipt of any information from BPV does not create an advisory, fiduciary, or client relationship. An advisory relationship is established only upon execution of a formal written engagement agreement between you and Blue Phoenix Ventures."
  }, {
    question: "No Guarantee of Results",
    answer: "Past transaction experience described on this Site is representative of prior engagements and does not guarantee future results. M&A transactions, capital raises, and strategic advisory engagements involve significant risk and uncertainty. Blue Phoenix Ventures makes no representation that any specific outcome will be achieved."
  }, {
    question: "Intellectual Property",
    answer: "All content on this Site — including text, graphics, logos, case studies, and marketing materials — is the property of Blue Phoenix Ventures, LLC or its licensors and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works from any content on this Site without our prior written consent."
  }, {
    question: "User Conduct",
    answer: "You agree not to use this Site to: (a) violate any applicable law or regulation; (b) transmit any harmful, offensive, or unsolicited communications; (c) attempt to gain unauthorized access to any system or data; or (d) impersonate any person or entity."
  }, {
    question: "Third-Party Links",
    answer: "This Site may link to third-party websites including FINRA BrokerCheck, GT Securities, and other resources. We are not responsible for the content, accuracy, or practices of third-party sites."
  }, {
    question: "Limitation of Liability",
    answer: "To the fullest extent permitted by law, Blue Phoenix Ventures, LLC and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, this Site or its content."
  }, {
    question: "Governing Law",
    answer: "These Terms are governed by the laws of the State of California, without regard to its conflict of laws principles. Any disputes arising under these Terms shall be resolved in the courts of Orange County, California."
  }, {
    question: "Changes to These Terms",
    answer: "We reserve the right to modify these Terms at any time. Updated Terms will be posted on this page with the revised effective date. Continued use of the Site following any changes constitutes your acceptance of the updated Terms."
  }, {
    question: "Contact Us",
    answer: "Questions regarding these Terms may be directed to: Blue Phoenix Ventures, LLC\xA0 |\xA0 Newport Beach, California\xA0 |\xA0 Email: Jeff@BPVConsulting.com"
  }];
  return <section data-section-id="1474" id="terms-sections" className="relative overflow-hidden bg-background text-foreground py-24 md:py-36">
      {}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-primary" />
      <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-2">
        <span className="block h-2 w-2 rounded-lg bg-chart-2" />
        <span className="block h-2 w-2 rounded-lg bg-chart-2" />
        <span className="block h-2 w-2 rounded-lg bg-chart-2" />
      </div>

      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative mx-auto max-w-[920px] px-6 md:px-14 transition-all duration-[620ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="relative text-center">
          {}
          

          <h2 className="mt-8 font-default font-extrabold text-foreground leading-[0.95] tracking-tight text-[clamp(2.1rem,5.2vw,4.5rem)]">Terms of Service</h2>

          <p className="mx-auto mt-8 max-w-[620px] font-default text-foreground/80 text-[clamp(1.05rem,1.2vw,1.2rem)] leading-relaxed">Effective Date: 6/9/2026&nbsp; |&nbsp; Last Updated: 6/9/2026<br /><br />These Terms of Service (Terms) govern your access to and use of bpvconsulting.com (the Site), operated by Blue Phoenix Ventures, LLC (Blue Phoenix Ventures, BPV, we, or us). By accessing or using the Site, you agree to be bound by these Terms.<br /><br /></p>
        </div>

        {}
        <div className="relative mt-16 md:mt-20">
          {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return <div key={i} data-index={i} className="relative border-b border-foreground/20 last:border-b-0">
                {}
                <span aria-hidden="true" className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] bg-chart-2 transition-all duration-[320ms] ease-out ${isOpen ? "h-1/2 opacity-100" : "h-0 opacity-0"}`} />

                <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} className="group flex w-full items-center justify-between gap-6 py-7 pl-5 pr-1 text-left transition-colors">
                  <h4 className="font-default font-semibold text-foreground text-[clamp(1.15rem,1.6vw,1.4rem)] leading-snug">
                    {faq.question}
                  </h4>
                  <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center text-chart-2 transition-transform duration-[240ms] ease-out ${isOpen ? "rotate-45" : "rotate-0"}`} aria-hidden="true">
                    <Plus className="h-6 w-6" strokeWidth={2.25} />
                  </span>
                </button>

                <div className={`grid overflow-hidden transition-all duration-[360ms] ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="min-h-0">
                    <p className="pb-7 pl-5 pr-10 font-default text-foreground/75 text-[clamp(0.95rem,1vw,1.02rem)] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {}
        <p className="mt-14 text-center font-mono text-[0.72rem] uppercase tracking-[0.18em] text-foreground/60">Need further guidance? Contact us at Jeff@BPVConsulting.com</p>
      </div>
    </section>;
}